import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { dayKey, formatDuration, formatMoney, formatMoneyCompact, holdMs, netPnl, tradingDayKey, usd } from './pnl';
import { tradeTime } from './tradeTime';
import { TagChip, tagKind } from './tags';
import { GradeBadge } from './grades';
import { balanceOnDay } from './balance';
import { noteHasContent } from './Routine';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { showResult, tradeMoves, useDisplayMode } from './display';
import { DayMarketEvents, EVENT_KINDS, EVENT_KIND_OPTIONS, ofKind, useMarketCalendar } from './MarketCalendar';
import { DayReminders, useReminders } from './Reminders';
import { ShotThumbs, useScreenshotUrls } from './Screenshots';
import { trackEvent } from './analytics';
import { dayInsights } from './dayInsights';
import DayInsightsCard, { DayInsightBadge } from './DayInsightsCard';
import { LinkBar, LinkChip, linkGroups, useTradeSelection } from './LinkedTrades';

// Short weekday names (Sun–Sat) in the current language. Jan 4, 2026 was a Sunday.
function weekdayNames() {
  return Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'short' }));
}
const emptyNote = { plan: '', review: '' };

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function shiftDay(key, n) {
  const d = new Date(`${key}T12:00:00`);
  d.setDate(d.getDate() + n);
  return dayKey(d);
}

function pnlClass(value) {
  if (value > 0) return 'gain';
  if (value < 0) return 'loss';
  return '';
}

// Rows of 7 days (Sun–Sat) covering the whole month, padded with days from
// the months before and after.
function buildWeeks(month) {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const weeks = [];
  const cursor = new Date(start);
  do {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  } while (cursor.getMonth() === month.getMonth());
  return weeks;
}

// `notes` maps YYYY-MM-DD to that day's { plan, review }; it is loaded by the
// journal so the sidebar can show today's plan too. Clicking a day opens it in a
// pop-up with its trades and notes. `jumpTo` ({ key, n }) asks the calendar to
// open a given day, e.g. from the sidebar's "Edit plan" button.
// The chart screenshots of a day's trades, above the plan and review.
function DayScreenshots({ trades }) {
  const { t } = useI18n();
  const urls = useScreenshotUrls(trades);
  const withShots = trades.filter((x) => x.screenshots && x.screenshots.length);
  if (!withShots.length) return null;
  return (
    <section className="day-shots" aria-label={t('Chart screenshots')}>
      <span className="modal-section-label">{t('Chart screenshots')}</span>
      {withShots.map((trade) => {
        const time = tradeTime(trade);
        const caption = `${time ? `${time} · ` : ''}${trade.symbol} · ${t(trade.side || 'Long')}`;
        const shots = trade.screenshots.map((s) => (urls[trade._id] || []).find((u) => u.id === s.id) || { id: s.id });
        return (
          <div key={trade._id} className="day-shots-trade">
            <span className="day-shots-caption">{caption}</span>
            <ShotThumbs shots={shots} caption={caption} />
          </div>
        );
      })}
    </section>
  );
}

// The whole year at a glance: 12 small months, each traded day green or red. A day opens its
// pop-up; a month's name opens that month.
function YearView({ year, days, today, onOpenDay, onOpenMonth, show }) {
  const { t } = useI18n();
  const names = weekdayNames().map((n) => n.slice(0, 2));
  return (
    <div className="year-grid">
      {[...Array(12)].map((_, m) => {
        const first = new Date(year, m, 1);
        const count = new Date(year, m + 1, 0).getDate();
        const cells = [...Array(first.getDay()).fill(null), ...Array.from({ length: count }, (__, i) => i + 1)];
        const prefix = `${year}-${String(m + 1).padStart(2, '0')}`;
        const monthShown = show(sumDays(Object.entries(days).filter(([k]) => k.startsWith(prefix)).map(([, d]) => d)));
        const traded = Object.keys(days).some((k) => k.startsWith(prefix));
        return (
          <div key={m} className="year-month">
            <button type="button" className="year-month-title" onClick={() => onOpenMonth(first)}>
              <span>{first.toLocaleDateString(currentLocale(), { month: 'long' })}</span>
              {traded && <span className={`year-month-net ${monthShown.tone}`}>{monthShown.text}</span>}
            </button>
            <div className="year-month-grid">
              {names.map((n, i) => <span key={`${n}${i}`} className="year-weekday">{n}</span>)}
              {cells.map((d, i) => {
                if (!d) return <span key={`e${i}`} />;
                const key = `${prefix}-${String(d).padStart(2, '0')}`;
                const day = days[key];
                const cls = ['year-day', day && (day.net > 0 ? 'day-gain' : day.net < 0 ? 'day-loss' : 'day-flat'), key === today && 'today'].filter(Boolean).join(' ');
                return day ? (
                  <button key={key} type="button" className={cls} onClick={() => onOpenDay(key)}
                    title={[...new Set(show(sumDays([day]), false).text ? [show(sumDays([day]), false).text, formatMoney(day.net)] : []),
                      t(day.trades.length === 1 ? '{n} trade' : '{n} trades', { n: day.trades.length })].join(' · ')}>{d}</button>
                ) : <span key={key} className={cls}>{d}</span>;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CAL_METRIC_KEY = 'mm-calendar-metric';
const CAL_EVENTS_KEY = 'mm-calendar-events';
// What the calendar shows in each day, week and month besides dollars.
// (P&L follows "Show amounts as" at the top of the page: dollars, %, points, ticks or hidden.)
const METRICS = [
  ['pnl', 'P&L'],
  ['winrate', 'Win rate'],
  ['avg', 'Average per trade'],
  ['pf', 'Profit factor'],
  ['wl', 'Wins / losses'],
  ['trades', 'Trades'],
];

// Adds up days ({ net, trades, wins, losses, grossWin, grossLoss }) into one total.
function sumDays(list) {
  const total = { net: 0, count: 0, wins: 0, losses: 0, grossWin: 0, grossLoss: 0, trades: [] };
  for (const d of list) {
    if (!d) continue;
    total.net += d.net;
    total.count += d.trades.length;
    total.wins += d.wins;
    total.losses += d.losses;
    total.grossWin += d.grossWin;
    total.grossLoss += d.grossLoss;
    total.trades.push(...d.trades);
  }
  return total;
}

// The number to show for a total in the chosen metric, and whether to color it green/red.
function metricValue(metric, total, size, t, compact, display) {
  const money = compact ? formatMoneyCompact : formatMoney;
  switch (metric) {
    case 'winrate':
      return { text: total.count ? `${Math.round((total.wins / total.count) * 100)}%` : '—', tone: pnlClass(total.net) };
    case 'avg':
      return { text: total.count ? money(total.net / total.count) : '—', tone: pnlClass(total.net) };
    case 'pf': {
      if (!total.grossLoss) return { text: total.grossWin ? '∞' : '—', tone: total.grossWin ? 'gain' : '' };
      const pf = total.grossWin / total.grossLoss;
      return { text: pf.toFixed(2), tone: pf >= 1 ? 'gain' : 'loss' };
    }
    case 'wl':
      return { text: t('{w}W / {l}L', { w: total.wins, l: total.losses }), tone: '' };
    case 'trades':
      return { text: String(total.count), tone: '' };
    default:
      // With Privacy on, the squares keep their color and show no amount.
      if (display === 'privacy') return { text: '', tone: pnlClass(total.net) };
      return showResult({ net: total.net, moves: tradeMoves(total.trades), size, money, t, mode: display });
  }
}

const CAL_VIEW_KEY = 'mm-calendar-view';

// Beside the calendar: the month (or year) being shown, in numbers.
function Overview({ days, prefix, prevPrefix, title }) {
  const { t } = useI18n();
  const list = Object.entries(days).filter(([k]) => k.startsWith(prefix)).map(([k, d]) => ({ key: k, ...d }));
  const prev = prevPrefix ? Object.entries(days).filter(([k]) => k.startsWith(prevPrefix)).reduce((sum, [, d]) => sum + d.net, 0) : null;
  const net = list.reduce((sum, d) => sum + d.net, 0);
  const trades = list.reduce((sum, d) => sum + d.trades.length, 0);
  const wins = list.reduce((sum, d) => sum + d.wins, 0);
  const green = list.filter((d) => d.net > 0).length;
  const best = [...list].sort((a, b) => b.net - a.net)[0];
  const worst = [...list].sort((a, b) => a.net - b.net)[0];
  const label = (k) => new Date(`${k}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
  const bar = (share, tone) => <span className="cal-ov-bar"><span className={tone} style={{ width: `${Math.round(share * 100)}%` }} /></span>;
  return (
    <aside className="cal-overview" aria-label={title}>
      <h3>{title}</h3>
      {!list.length ? <p className="empty">{t('No trades in this period.')}</p> : (
        <>
          <div className="cal-ov-main">
            <span className="cal-ov-label">{t('Net P&L')}</span>
            <strong className={`cal-ov-net ${pnlClass(net)}`}>{formatMoney(net)}</strong>
            {prev !== null && prev !== 0 && <span className={`cal-ov-change ${pnlClass(net - prev)}`}>{net - prev >= 0 ? '▲' : '▼'} {formatMoney(net - prev)} {t('vs the period before')}</span>}
          </div>
          <div className="cal-ov-row"><span>{t('Trades')}</span><strong>{trades}</strong></div>
          <div className="cal-ov-row"><span>{t('Win rate')}</span><strong>{trades ? Math.round((wins / trades) * 100) : 0}%</strong></div>
          {bar(trades ? wins / trades : 0, 'gain')}
          <div className="cal-ov-row"><span>{t('Green days')}</span><strong>{green}/{list.length}</strong></div>
          {bar(list.length ? green / list.length : 0, 'gain')}
          <div className="cal-ov-row"><span>{t('Average per day')}</span><strong className={pnlClass(net)}>{formatMoney(net / list.length)}</strong></div>
          {best && best.net > 0 && <div className="cal-ov-row"><span>{t('Best day')}</span><strong className="gain">{label(best.key)} · {formatMoney(best.net)}</strong></div>}
          {worst && worst.net < 0 && <div className="cal-ov-row"><span>{t('Worst day')}</span><strong className="loss">{label(worst.key)} · {formatMoney(worst.net)}</strong></div>}
        </>
      )}
    </aside>
  );
}

// insights: { preview, mark, accounts } to show Day insights (null hides them).
function Calendar({ trades, account, size, library, notes, setNotes, jumpTo, startDay, onEditTrade, onAddTrade, onSessionExpired, overview = false, insights = null }) {
  const { t } = useI18n();
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(CAL_VIEW_KEY) === 'year' ? 'year' : 'month'; } catch { return 'month'; }
  });
  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem(CAL_VIEW_KEY, v); } catch { /* not remembered */ }
  };
  const display = useDisplayMode();
  const [metricChoice, setMetricChoice] = useState(() => {
    try { return localStorage.getItem(CAL_METRIC_KEY) || 'pnl'; } catch { return 'pnl'; }
  });
  const chooseMetric = (m) => {
    setMetricChoice(m);
    try { localStorage.setItem(CAL_METRIC_KEY, m); } catch { /* not remembered */ }
  };
  const metric = METRICS.some(([k]) => k === metricChoice) ? metricChoice : 'pnl';
  const show = (total, compact = true) => metricValue(metric, total, size, t, compact, display);
  const hideTotal = metric === 'pnl' && display === 'privacy';
  // Under the big number: the trade count, or the dollars when the big number is the count.
  const subLine = (total) => ((metric === 'trades' || metric === 'wl') ? formatMoneyCompact(total.net) : t(total.count === 1 ? '{n} trade' : '{n} trades', { n: total.count }));
  const today = dayKey(new Date());
  // Opens on `startDay`'s month when given (the Overview dashboard: the latest trade's month).
  const [month, setMonth] = useState(() => startOfMonth(startDay ? new Date(`${startDay}T12:00:00`) : new Date()));
  const [selected, setSelected] = useState(today);
  const insightsByDay = useMemo(() => (insights ? dayInsights(trades, { accounts: insights.accounts || [], notes }) : null), [insights, trades, notes]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyNote);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [saveError, setSaveError] = useState('');

  // Load the selected day's saved note into the editor.
  const savedNote = notes[selected] || emptyNote;
  useEffect(() => {
    setDraft({ plan: savedNote.plan, review: savedNote.review });
  }, [selected, savedNote.plan, savedNote.review]);

  useEffect(() => {
    setSaveState('idle');
  }, [selected]);

  // Trades grouped by the day they happened, with each day's totals.
  const days = useMemo(() => {
    const map = {};
    for (const t of trades) {
      const key = tradingDayKey(t.date);
      if (!map[key]) map[key] = { trades: [], net: 0, wins: 0, losses: 0, grossWin: 0, grossLoss: 0 };
      const net = netPnl(t);
      map[key].trades.push(t);
      map[key].net += net;
      if (net > 0) { map[key].wins += 1; map[key].grossWin += net; }
      if (net < 0) { map[key].losses += 1; map[key].grossLoss -= net; }
    }
    for (const day of Object.values(map)) {
      day.trades.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return map;
  }, [trades]);

  const weeks = buildWeeks(month);
  // Market events (economic reports and big earnings) on the calendar, when switched on.
  // 'off', or which market events to show: 'all', 'econ' (economic data) or 'earnings'.
  const [eventKind, setEventKind] = useState(() => {
    try {
      const v = localStorage.getItem(CAL_EVENTS_KEY);
      return v === 'on' ? 'all' : EVENT_KINDS.some(([id]) => id === v) ? v : 'off';
    } catch { return 'off'; }
  });
  const chooseEventKind = (v) => { setEventKind(v); try { localStorage.setItem(CAL_EVENTS_KEY, v); } catch { /* not remembered */ } };
  const showEvents = eventKind !== 'off';
  const gridFrom = dayKey(weeks[0][0]);
  const gridTo = dayKey(weeks[weeks.length - 1][6]);
  const market = useMarketCalendar(showEvents && view === 'month' ? gridFrom : null, gridTo);
  const eventsOn = {};
  // From today on: past days would be missing the official reports from before the schedule starts.
  for (const e of ((market && market.events) || []).filter(ofKind(eventKind))) if (e.date >= today) (eventsOn[e.date] = eventsOn[e.date] || []).push(e);
  // Personal reminders on each day.
  const reminders = useReminders(view === 'month' ? gridFrom : null, gridTo);
  const remindersOn = (key) => (reminders || []).filter((r) => r.date === key).sort((a, b) => a.time.localeCompare(b.time));
  const remTime = (hhmm) => new Date(2026, 0, 1, ...hhmm.split(':').map(Number)).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });
  const cellEvents = (key) => (eventsOn[key] || []).filter((e) => e.impact !== 'low' && e.type !== 'fomcPress');
  const monthPrefix = dayKey(month).slice(0, 7);
  const monthDays = Object.entries(days).filter(([key]) => key.startsWith(monthPrefix));
  const monthShown = show(sumDays(monthDays.map(([, d]) => d)), false);
  const monthTrades = monthDays.reduce((sum, [, d]) => sum + d.trades.length, 0);
  const greenDays = monthDays.filter(([, d]) => d.net > 0).length;

  const isDirty = draft.plan !== savedNote.plan || draft.review !== savedNote.review;

  async function saveNote(date, note) {
    setSaveState('saving');
    setSaveError('');
    try {
      const saved = await api(`/notes/${date}`, { method: 'PUT', body: note });
      setNotes((prev) => {
        const next = { ...prev };
        if (noteHasContent(saved)) next[date] = saved;
        else delete next[date];
        return next;
      });
      setSaveState('saved');
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setSaveError(err.message);
      setSaveState('error');
    }
  }

  // Unsaved notes are saved automatically when switching to another day.
  function selectDay(key) {
    if (key === selected) return;
    if (isDirty) saveNote(selected, draft);
    setSelected(key);
  }

  function openDay(key) {
    trackEvent('Calendar day');
    selectDay(key);
    setOpen(true);
  }

  // Steps to the previous or next day inside the pop-up, following it across months.
  function stepDay(n) {
    const key = shiftDay(selected, n);
    selectDay(key);
    setMonth(startOfMonth(new Date(`${key}T12:00:00`)));
  }

  function closeDay() {
    if (isDirty) saveNote(selected, draft);
    setOpen(false);
  }

  useEffect(() => {
    if (!jumpTo) return;
    setMonth(startOfMonth(new Date(`${jumpTo.key}T12:00:00`)));
    openDay(jumpTo.key);
  }, [jumpTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes the pop-up, unless a trade is open on top of it (that closes first).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key !== 'Escape' || document.querySelectorAll('.modal-backdrop').length > 1) return;
      closeDay();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  function goToMonth(n) {
    setMonth((m) => addMonths(m, view === 'year' ? n * 12 : n));
  }

  const selectedDate = new Date(`${selected}T12:00:00`);
  const selectedDay = days[selected];
  const dayTrades = useMemo(() => (selectedDay ? selectedDay.trades : []), [selectedDay]);
  const picking = useTradeSelection(dayTrades);
  const groups = useMemo(() => linkGroups(dayTrades), [dayTrades]);
  const endOfDayBalance = selectedDay ? balanceOnDay(account, trades, selected) : null;

  return (
    <section className="calendar" id="calendar">
      <div className="calendar-header">
        <div>
          <h2>{t('Calendar')}</h2>
          <div className="calendar-month-row">
            <button type="button" className="secondary small calendar-step" onClick={() => goToMonth(-1)} aria-label={view === 'year' ? t('Previous year') : t('Previous month')}>
              &larr;
            </button>
            <div className="calendar-month">
              {view === 'year' ? month.getFullYear() : month.toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" className="secondary small calendar-step" onClick={() => goToMonth(1)} aria-label={view === 'year' ? t('Next year') : t('Next month')}>
              &rarr;
            </button>
          </div>
        </div>
        {view === 'year' ? (() => {
          const yearDays = Object.entries(days).filter(([k]) => k.startsWith(String(month.getFullYear())));
          return (
            <div className="calendar-month-stats">
              {!hideTotal && <span>{t('Year')} <strong className={show(sumDays(yearDays.map(([, d]) => d)), false).tone}>{show(sumDays(yearDays.map(([, d]) => d)), false).text}</strong></span>}
              {metric !== 'trades' && <span>{t('{n} trades', { n: yearDays.reduce((sum, [, d]) => sum + d.trades.length, 0) })}</span>}
              <span>{t('{green}/{days} green days', { green: yearDays.filter(([, d]) => d.net > 0).length, days: yearDays.length })}</span>
            </div>
          );
        })() : (
        <div className="calendar-month-stats">
          {!hideTotal && (
            <span>
              {t('Month')} <strong className={monthShown.tone}>{monthShown.text}</strong>
            </span>
          )}
          {metric !== 'trades' && <span>{t('{n} trades', { n: monthTrades })}</span>}
          <span>{t('{green}/{days} green days', { green: greenDays, days: monthDays.length })}</span>
        </div>
        )}
        <div className="calendar-nav">
          <label className="calendar-metric">
            <select value={metric} onChange={(e) => chooseMetric(e.target.value)} aria-label={t('What each day shows')}>
              {METRICS.map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
            </select>
          </label>
          <div className="inbox-filter calendar-view" role="group" aria-label={t('Calendar view')}>
            <button type="button" className={`secondary small${view === 'month' ? ' active' : ''}`} onClick={() => chooseView('month')}>{t('Month')}</button>
            <button type="button" className={`secondary small${view === 'year' ? ' active' : ''}`} onClick={() => chooseView('year')}>{t('Year')}</button>
          </div>
          {view === 'month' && (
            <label className="calendar-metric cal-events-pick">
              <select value={eventKind} onChange={(ev) => chooseEventKind(ev.target.value)} aria-label={t('Market events')}>
                <option value="off">{t('No market events')}</option>
                {EVENT_KIND_OPTIONS.map(([id, label]) => <option key={id} value={id}>{t(label)}</option>)}
              </select>
            </label>
          )}
          {onAddTrade && (
            <button type="button" className="small calendar-add" onClick={() => onAddTrade(today)}>+ {t('Add a trade')}</button>
          )}
        </div>
      </div>

      <div className={overview ? 'calendar-layout' : undefined}>
      <div className="calendar-main">
      {view === 'year' && (
        <YearView year={month.getFullYear()} days={days} today={today} onOpenDay={openDay} show={show}
          onOpenMonth={(m) => { setMonth(m); chooseView('month'); }} />
      )}
      {view === 'month' && (
      <div className="calendar-grid" role="grid">
        {weekdayNames().map((d) => (
          <div key={d} className="calendar-weekday">{d}</div>
        ))}
        <div className="calendar-weekday week-col">{t('Week total')}</div>

        {weeks.map((week) => {
          const weekTotal = sumDays(week.map((d) => days[dayKey(d)]));
          const weekShown = show(weekTotal);
          return [
            ...week.map((date) => {
              const key = dayKey(date);
              const day = days[key];
              const classes = [
                'calendar-day',
                date.getMonth() !== month.getMonth() && 'outside',
                key === today && 'today',
                open && key === selected && 'selected',
                day && (day.net > 0 ? 'day-gain' : day.net < 0 ? 'day-loss' : ''),
              ].filter(Boolean).join(' ');
              return (
                <button key={key} type="button" className={classes} onClick={() => openDay(key)}
                  aria-label={`${date.toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' })}${day ? `, ${formatMoney(day.net)}, ${t(day.trades.length === 1 ? '{n} trade' : '{n} trades', { n: day.trades.length })}` : ''}`}>
                  <span className="day-top">
                    <span className="day-number">{date.getDate()}</span>
                    {noteHasContent(notes[key]) && <span className="note-dot" title={t('Has notes')} />}
                    {insightsByDay && <DayInsightBadge entry={insightsByDay[key]} />}
                  </span>
                  {remindersOn(key).length > 0 && (
                    <span className={`cal-reminder${remindersOn(key).every((r) => r.done) ? ' done' : ''}`} title={remindersOn(key).map((r) => `${remTime(r.time)} ${r.note}`).join('\n')}>
                      ⏰ {remTime(remindersOn(key)[0].time)}{remindersOn(key).length > 1 ? ` +${remindersOn(key).length - 1}` : ''}
                    </span>
                  )}
                  {cellEvents(key).length > 0 && (
                    <span className="cal-mkt-list">
                      {cellEvents(key).slice(0, 2).map((e) => (
                        <span key={e.id} className={`cal-mkt impact-${e.impact}${e.kind === 'market' ? ' closed' : ''}`}>{e.kind === 'market' ? t(e.short) : e.short}</span>
                      ))}
                      {cellEvents(key).length > 2 && <span className="cal-mkt more">+{cellEvents(key).length - 2}</span>}
                    </span>
                  )}
                  {day && (
                    <>
                      <span className={`day-pnl ${show(sumDays([day])).tone}`}>{show(sumDays([day])).text}</span>
                      <span className="day-count">{subLine(sumDays([day]))}</span>
                    </>
                  )}
                </button>
              );
            }),
            <div key={`week-${dayKey(week[0])}`} className="calendar-week-total week-col">
              {weekTotal.count > 0 && (
                <>
                  <span className={`day-pnl ${weekShown.tone}`}>{weekShown.text}</span>
                  <span className="day-count">{subLine(weekTotal)}</span>
                </>
              )}
            </div>,
          ];
        })}
      </div>
      )}
      </div>
      {overview && (view === 'year'
        ? <Overview days={days} prefix={String(month.getFullYear())} prevPrefix={String(month.getFullYear() - 1)} title={t('{year} overview', { year: month.getFullYear() })} />
        : <Overview days={days} prefix={monthPrefix} prevPrefix={dayKey(addMonths(month, -1)).slice(0, 7)}
          title={t('{month} overview', { month: month.toLocaleDateString(currentLocale(), { month: 'long' }) })} />)}
      </div>

      {open && (
      <div className="modal-backdrop day-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeDay()}>
      <div className="modal modal-wide day-modal day-panel" role="dialog" aria-modal="true" aria-labelledby="day-modal-title">
        <div className="day-modal-top">
          <div className="calendar-nav">
            <button type="button" className="secondary small" onClick={() => stepDay(-1)} aria-label={t('Previous day')}>&larr;</button>
            <button type="button" className="secondary small" onClick={() => stepDay(1)} aria-label={t('Next day')}>&rarr;</button>
          </div>
          <button type="button" className="modal-close" onClick={closeDay} aria-label={t('Close')}>&times;</button>
        </div>
        <div className="day-panel-header">
          <h2 id="day-modal-title">{selectedDate.toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h2>
          {selectedDay && (
            <div className="day-panel-stats">
              <span className={pnlClass(selectedDay.net)}>{formatMoney(selectedDay.net)}</span>
              <span>{t(selectedDay.trades.length === 1 ? '{n} trade' : '{n} trades', { n: selectedDay.trades.length })}</span>
              <span>{t('{w}W / {l}L', { w: selectedDay.wins, l: selectedDay.trades.length - selectedDay.wins })}</span>
              {endOfDayBalance !== null && (
                <span>{t('Balance {amount}', { amount: usd.format(endOfDayBalance) })}</span>
              )}
            </div>
          )}
        </div>

        {insightsByDay && insightsByDay[selected] && (
          <DayInsightsCard entry={insightsByDay[selected]} preview={insights.preview} canAskMark={insights.mark}
            date={selectedDate.toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' })} />
        )}
        <DayReminders date={selected} reminders={reminders} />
        {showEvents && <DayMarketEvents events={eventsOn[selected]} />}

        {selectedDay ? (
          <>
          <div className="day-modal-row">
            <p className="day-modal-hint">{t('Click a trade to tag it, add notes or see all its details. Tick several to link them as one trade idea or tag them all at once.')}</p>
            {selectedDay.trades.length > 1 && <button type="button" className="link-button" onClick={picking.all}>{picking.selected.size === selectedDay.trades.length ? t('Clear') : t('Select all')}</button>}
            {onAddTrade && <button type="button" className="secondary small" onClick={() => onAddTrade(selected)}>+ {t('Add a trade manually')}</button>}
          </div>
          <LinkBar trades={selectedDay.trades} selected={picking.selected} onClear={picking.clear} library={library} />
          <ul className="day-trades">
            {selectedDay.trades.map((trade) => {
              const net = netPnl(trade);
              return (
                <li key={trade._id} className={`lt-li${picking.selected.has(trade._id) ? ' picked' : ''}${trade.linkId && groups[trade.linkId] ? ' linked' : ''}`}
                  style={trade.linkId && groups[trade.linkId] ? { '--lt': groups[trade.linkId].color } : undefined}>
                  <label className="lt-check" title={t('Select this trade')}>
                    <input type="checkbox" checked={picking.selected.has(trade._id)} onChange={() => picking.toggle(trade._id)} aria-label={t('Select {symbol} at {time}', { symbol: trade.symbol, time: tradeTime(trade) || '' })} />
                  </label>
                  <button type="button" className="day-trade-row" onClick={() => onEditTrade(trade)}
                    title={t('Edit, tag or delete this trade')}>
                    <span className="day-trade-time">
                      {/* Evening trades belong to the next trading day; show their own weekday. */}
                      {dayKey(trade.date) !== selected && <>{new Date(trade.date).toLocaleDateString(currentLocale(), { weekday: 'short' })} </>}
                      {tradeTime(trade) || '—'}
                    </span>
                    <span className="day-trade-symbol">{trade.symbol}</span>
                    <span className="day-trade-side">
                      {t(trade.side || 'Long')} &times; {trade.size || 1}
                      {holdMs(trade) !== null && <span className="day-trade-hold" title={t('Time in the trade')}> &middot; {formatDuration(holdMs(trade))}</span>}
                    </span>
                    <span className="day-trade-tags">
                      <LinkChip trade={trade} groups={groups} onPick={picking.pickGroup} />
                      <GradeBadge grade={trade.grade} />
                      {(trade.tags || []).map((name) => (
                        <TagChip key={name} name={name} kind={tagKind(name, library)} />
                      ))}
                      {(!trade.tags || trade.tags.length === 0) && <span className="add-tag-hint">+ {t('Tag')}</span>}
                    </span>
                    <span className={`day-trade-pnl ${pnlClass(net)}`}>{formatMoney(net)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          </>
        ) : (
          <div className="day-modal-row">
            <p className="empty">{t('No trades on this day. You can still write your plan or review below.')}</p>
            {onAddTrade && <button type="button" className="small" onClick={() => onAddTrade(selected)}>+ {t('Add a trade manually')}</button>}
          </div>
        )}

        {selectedDay && <DayScreenshots trades={selectedDay.trades} />}

        <div className="day-notes">
          <label>
            {t('Trade plan')}
            <textarea
              rows="4"
              value={draft.plan}
              onChange={(e) => setDraft({ ...draft, plan: e.target.value })}
              placeholder={t("Key levels, bias, setups you're watching, max loss for the day…")}
            />
          </label>
          <label>
            {t('Review')}
            <textarea
              rows="4"
              value={draft.review}
              onChange={(e) => setDraft({ ...draft, review: e.target.value })}
              placeholder={t('Did you follow your plan? What went well, what would you do differently?')}
            />
          </label>
        </div>
        <div className="day-notes-actions">
          <button type="button" onClick={() => saveNote(selected, draft)} disabled={!isDirty || saveState === 'saving'}>
            {saveState === 'saving' ? t('Saving…') : t('Save notes')}
          </button>
          {isDirty && saveState !== 'saving' && <span className="save-status">{t('Unsaved changes')}</span>}
          {!isDirty && saveState === 'saved' && <span className="save-status success">{t('Saved')}</span>}
          {saveState === 'error' && <span className="save-status error">{saveError}</span>}
        </div>
      </div>
      </div>
      )}
    </section>
  );
}

export default Calendar;
